import classNames from "classnames";
import dayjs from "dayjs";
import { ReactNode, useEffect, useState } from "react";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { CartModal } from "components/CartModal/CartModal";
import {
  SKELETON_MIN_VISIBLE_MS,
  SKELETON_SHOW_DELAY_MS,
  TIMELINE_DETAIL_HINT,
  TIMELINE_EMPTY_SUBTEXT,
  type TimelineLegendFilter,
} from "constants/filingsTimeline";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useDelayedLoading } from "hooks/useDelayedLoading";
import {
  useGetFilingsTimelineMetaQuery,
  useGetFilingsTimelineQuery,
  usePrefetch,
  type TimelineFilingRow,
} from "store/apis/filingsTimeline";
import { getFilingRowKey } from "utils/filingsTimeline/getFilingRowKey";
import { getLegendFilterHint } from "utils/filingsTimeline/getLegendFilterHint";
import { getOverdueAcrossYears } from "utils/filingsTimeline/getOverdueAcrossYears";
import { getTimelineYearBounds } from "utils/filingsTimeline/getTimelineYearBounds";
import { getYearsWithFilings } from "utils/filingsTimeline/getYearsWithFilings";
import { matchesLegendFilter } from "utils/filingsTimeline/matchesLegendFilter";
import { TimelineContext } from "./TimelineContext";
import { TimelineEmptyState } from "./TimelineEmptyState";
import { TimelineFilingDetail } from "./TimelineFilingDetail";
import { TimelineHeader } from "./TimelineHeader";
import { TimelineSkeleton } from "./TimelineSkeleton";
import { TimelineTrack } from "./TimelineTrack";

type FilingsTimelineProps = {
  entityIds: string[];
};

const ERROR_MESSAGE = "Could not load your filings timeline";

const NO_FILINGS: TimelineFilingRow[] = [];

const TimelineCard = ({ children }: { children: ReactNode }) => (
  <div className="t-overflow-hidden t-rounded-xl t-border t-border-solid t-border-neutral-10 t-bg-surface">
    {children}
  </div>
);

export const FilingsTimeline = ({ entityIds }: FilingsTimelineProps) => {
  const { uuid: groupId } = useCurrentGroupContext();

  const [requestedYear, setRequestedYear] = useState(dayjs().year());
  const [selectedRowKey, setSelectedRowKey] = useState<string | null>(null);
  const [isIntroAnimated, setIsIntroAnimated] = useState(true);
  const [cartEntityId, setCartEntityId] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<TimelineLegendFilter | null>(
    null
  );
  const [previewFilter, setPreviewFilter] =
    useState<TimelineLegendFilter | null>(null);

  const {
    data: meta,
    isLoading: isMetaLoading,
    isError: isMetaError,
  } = useGetFilingsTimelineMetaQuery({ groupId }, { skip: !groupId });

  const metaEntities = meta?.entities || [];

  const scopedEntities =
    entityIds.length > 0
      ? metaEntities.filter((entity) => entityIds.includes(entity.uuid))
      : metaEntities;

  const bounds = getTimelineYearBounds(metaEntities);

  let viewYear = requestedYear;
  if (bounds) {
    viewYear = Math.min(
      Math.max(requestedYear, bounds.minYear),
      bounds.maxYear
    );
  }

  const {
    data: rows,
    isLoading: isRowsLoading,
    isSuccess: isRowsSuccess,
    isError: isRowsError,
  } = useGetFilingsTimelineQuery(
    { groupId, year: viewYear, entityIds },
    { skip: !groupId }
  );

  const isLoading = isMetaLoading || isRowsLoading;

  const prefetchYear = usePrefetch("getFilingsTimeline");

  const entityIdsKey = entityIds.join(",");

  useEffect(() => {
    if (!groupId || isRowsLoading) return;

    const ids = entityIdsKey ? entityIdsKey.split(",") : [];

    prefetchYear({ groupId, year: viewYear - 1, entityIds: ids });
    prefetchYear({ groupId, year: viewYear + 1, entityIds: ids });
  }, [groupId, viewYear, entityIdsKey, isRowsLoading, prefetchYear]);

  const isSkeletonShown = useDelayedLoading(
    isLoading,
    SKELETON_SHOW_DELAY_MS,
    SKELETON_MIN_VISIBLE_MS
  );

  const years = getYearsWithFilings(scopedEntities);
  const overdue = getOverdueAcrossYears(scopedEntities, viewYear);

  // `uuid` repeats within a response, so the row's own key is what the widget
  // selects and renders on.
  const filings: TimelineFilingRow[] = (rows?.filings || NO_FILINGS).map(
    (filing, index) => ({ ...filing, rowKey: getFilingRowKey(filing, index) })
  );

  const visibleFilings = activeFilter
    ? filings.filter((filing) => matchesLegendFilter(filing, activeFilter))
    : filings;

  // Once a filter is applied the hidden filings are gone, so hovering
  // another status has nothing left to fade.
  const fadeFilter = activeFilter ? null : previewFilter;

  const selectedFiling =
    visibleFilings.find((filing) => filing.rowKey === selectedRowKey) || null;

  const detailHint = activeFilter
    ? getLegendFilterHint(activeFilter, visibleFilings.length, viewYear)
    : TIMELINE_DETAIL_HINT;

  const handleChangeYear = (year: number) => {
    setRequestedYear(year);
    setSelectedRowKey(null);
    setActiveFilter(null);
    setPreviewFilter(null);
    setIsIntroAnimated(true);
  };

  const handleApplyFilter = (filter: TimelineLegendFilter) => {
    const matches = filings.filter((filing) =>
      matchesLegendFilter(filing, filter)
    );

    const isSelectionKept = matches.some(
      (filing) => filing.rowKey === selectedRowKey
    );

    setActiveFilter(filter);
    setIsIntroAnimated(false);

    if (matches.length === 1) {
      setSelectedRowKey(matches[0].rowKey);
      return;
    }

    if (!isSelectionKept) setSelectedRowKey(null);
  };

  const handleClearFilter = () => {
    setActiveFilter(null);
    setIsIntroAnimated(false);
    setSelectedRowKey(null);
  };

  const handleSelectFiling = (filing: TimelineFilingRow) => {
    setSelectedRowKey((current) =>
      current === filing.rowKey ? null : filing.rowKey
    );
  };

  const clearSelection = () => setSelectedRowKey(null);

  const handleOpenCart = (entityId: string) => setCartEntityId(entityId);
  const closeCart = () => setCartEntityId(null);

  const handleJumpToOverdue = (side: "left" | "right") => {
    const jumpYear =
      side === "left" ? overdue.jumpYearBefore : overdue.jumpYearAfter;

    if (jumpYear) handleChangeYear(jumpYear);
  };

  if (!isLoading && !isMetaError && !bounds) {
    return (
      <TimelineCard>
        <TimelineEmptyState subtext={TIMELINE_EMPTY_SUBTEXT} />
      </TimelineCard>
    );
  }

  return (
    <TimelineContext.Provider
      value={{
        selectedRowKey,
        onSelectFiling: handleSelectFiling,
        onOpenCart: handleOpenCart,
        isIntroAnimated,
        activeFilter,
        fadeFilter,
        onApplyFilter: handleApplyFilter,
        onClearFilter: handleClearFilter,
        onPreviewFilter: setPreviewFilter,
      }}
    >
      <div>
        <Async.Root
          isLoading={isLoading || isSkeletonShown}
          isSuccess={isRowsSuccess && !isMetaError}
          isError={isMetaError || isRowsError}
          isEmpty={false}
          renderOnLoad={
            <div className={classNames({ "t-invisible": !isSkeletonShown })}>
              <TimelineSkeleton />
            </div>
          }
        >
          <Async.ErrorHandler>
            <TimelineCard>
              <div className="t-flex t-h-[149px] t-items-center t-justify-center t-text-body-sm t-text-neutral-40">
                {ERROR_MESSAGE}
              </div>
            </TimelineCard>
          </Async.ErrorHandler>

          <Async.Success>
            <TimelineCard>
              <div
                key={`header-${viewYear}`}
                className="t-animate-enter-down motion-reduce:t-animate-none"
              >
                <TimelineHeader
                  viewYear={viewYear}
                  filings={filings}
                  years={years}
                  minYear={bounds?.minYear || viewYear}
                  maxYear={bounds?.maxYear || viewYear}
                  hasAnyFiling={years.length > 0}
                  onChangeYear={handleChangeYear}
                />
              </div>

              {/* The track is a fixed-geometry chart — on a narrow viewport
                  it scrolls sideways rather than collapsing. */}
              <div className="hide-scrollbar t-overflow-x-auto">
                <TimelineTrack
                  key={`track-${viewYear}`}
                  filings={visibleFilings}
                  year={viewYear}
                  today={meta?.today || ""}
                  hasAnyFiling={years.length > 0}
                  overdueBefore={overdue.beforeCount}
                  overdueAfter={overdue.afterCount}
                  onJumpToOverdue={handleJumpToOverdue}
                  onChangeYear={handleChangeYear}
                />
              </div>

              <div
                key={`footer-${viewYear}`}
                className="t-animate-enter-up motion-reduce:t-animate-none"
              >
                <TimelineFilingDetail
                  filing={selectedFiling}
                  hint={detailHint}
                  onClose={clearSelection}
                />
              </div>
            </TimelineCard>
          </Async.Success>
        </Async.Root>

        {cartEntityId && (
          <CartModal
            open
            onClose={closeCart}
            entityIdFromProps={cartEntityId}
            shouldRedirectToTask={false}
            hideEntitySelector
          />
        )}
      </div>
    </TimelineContext.Provider>
  );
};
